import React, {useEffect, useMemo, useRef, useState} from "react";
import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import Button from "../../../../components/button/button.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import InputDatetime from "../../../../components/input/input-datetime.jsx";
import InputTextarea from "../../../../components/input/input-textarea.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import SentryRow from "../../../../components/template/sentry-row.jsx";
import Dropzone from "../../../../components/dropzone/dropzone.jsx";
import {useParams, useSearchParams} from "react-router";
import {FaPlus} from "react-icons/fa";
import {useQuery} from "@tanstack/react-query";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import projectUtils from "../../../../utils/project-utils.js";
import ScheduleGarrisonService from "../../../../services/dispatch/schedule-garrison-service.js";
import ScheduleGarrisonActivities from "./components/schedule-garrison-activities.jsx";
import ScheduleGarrisonHoursModal from "./components/schedule-garrison-hours-modal.jsx";
import ScheduleGarrisonAddressModal from "./components/schedule-garrison-address-modal.jsx";
import {
    WEEKDAYS, buildScheduleModel, emptyAddress, emptyHours, hasAddress, mapAddressFromPayload,
} from "./utils/schedule-garrison-utils.js";

const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;
const REQUIRED_BG = "!bg-[#fdfdee]";
const REQUIRED_LABEl_BG = "!bg-[linear-gradient(to_bottom,transparent_50%,#fdfdee_50%)]";

const initialState = {
    id: undefined, name: "", dateUsageMin: "", dateUsageMax: "", description: "",
    inactive: false, inactiveObservation: ""
};

const initialGroups = {weekdays: [], teams: [], intendedFunctions: [], sectors: [], autoApply: false};

const mapReasons = (list = []) => list.map(item => (typeof item === "string" ? {value: item, label: item} : {value: item.alias ?? item.name, label: item.alias ?? item.name}));
const mapPriorities = (list = []) => list.map(item => (typeof item === "string" ? {value: item, label: item} : {value: item.name, label: item.name}));
const mapIntended = (list = []) => list.map(item => (typeof item === "string" ? {value: item, label: item} : {value: item.key, label: item.name}));
const mapStrings = (list = []) => list.map(item => (typeof item === "string" ? {value: item, label: item} : {value: item.value ?? item.name ?? item.id, label: item.label ?? item.name ?? item.text}));

const ScheduleGarrisonForm = () => {
    const navigate = navigateHook();
    const {id} = useParams();
    const [searchParams] = useSearchParams();
    const copyId = searchParams.get("cp");
    const loadId = id ?? copyId;

    const [state, setState] = useState(initialState);
    const [groups, setGroups] = useState(initialGroups);
    const [activities, setActivities] = useState([]);
    const [attaches, setAttaches] = useState([]);
    const [attachesDescription, setAttachesDescription] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [hoursTempId, setHoursTempId] = useState(null);
    const [addressTempId, setAddressTempId] = useState(null);

    const tempKey = useRef(0);
    const nextTempId = () => `temp-${tempKey.current++}`;

    const {data: optionsData} = useQuery({
        queryFn: () => ScheduleGarrisonService.formData(),
        queryKey: ["schedule-garrison-options"],
    });

    const fd = optionsData?.data ?? optionsData ?? {};
    const reasonOptions = useMemo(() => mapReasons(fd.reasons), [optionsData]);
    const priorityOptions = useMemo(() => mapPriorities(fd.priorities), [optionsData]);
    const teamOptions = useMemo(() => mapStrings(fd.teams), [optionsData]);
    const intendedFunctionOptions = useMemo(() => mapIntended(fd.intendedFunctions), [optionsData]);
    const sectorOptions = useMemo(() => mapStrings(fd.sectors), [optionsData]);
    const entityCity = fd.entityCity ?? "";

    const {data, isLoading} = useQuery({
        queryFn: async () => {
            const raw = await ScheduleGarrisonService.get(loadId).catch(() => null);
            if (!raw) {
                navigate("/404");
                return;
            }

            const schedule = raw?.schedule ?? raw ?? {};
            const scheduleGroups = schedule.groups ?? {};

            setState({
                id: id ?? undefined,
                name: schedule.name ?? "",
                dateUsageMin: schedule.dateUsageMin ?? "",
                dateUsageMax: schedule.dateUsageMax ?? "",
                description: schedule.description ?? "",
                inactive: !!schedule.inactive,
                inactiveObservation: schedule.inactiveObservation ?? "",
                systemUpdate: schedule.systemUpdate
            });

            setGroups({
                weekdays: (scheduleGroups.weekdays ?? []).map(String),
                teams: scheduleGroups.teams ?? [],
                intendedFunctions: scheduleGroups.intendedFunctions ?? [],
                sectors: scheduleGroups.sectors ?? [],
                autoApply: !!schedule.autoApplyByGroup,
            });

            const activitiesData = await ScheduleGarrisonService.getActivities(loadId);

            setActivities((activitiesData ?? []).map(item => ({
                tempId: nextTempId(),
                itemId: id ? item.itemId : undefined,
                hours: item.hours ?? emptyHours(),
                duration: item.duration ?? "",
                reason: item.reason ?? "",
                priority: item.priority ?? "",
                description: item.description ?? "",
                address: mapAddressFromPayload(item.address ?? {}),
            })));

            if (id) {
                const attachList = schedule.attachment ?? [];
                const attachDescriptions = attachList.reduce((acc, attach) => {
                    const attachId = attach?.attachId ?? attach?.reference;
                    if (attachId) {
                        acc[attachId] = attach?.description ?? "";
                    }

                    return acc;
                }, {});

                setAttachesDescription(attachDescriptions);
                setAttaches(attachList.map(file => ({
                    id: file.id,
                    attachId: file.attachId ?? file.reference,
                    name: file.name,
                    extension: file.extension ?? file.type,
                })));
            }

            return raw;
        },
        queryKey: ["dispatch-schedule-garrison", loadId],
        enabled: !!loadId,
    });

    const onGroupChange = (key, selected) => setGroups(prev => ({...prev, [key]: (selected ?? []).map(option => option.value)}));

    const groupHasSelection = groups.weekdays.length || groups.teams.length || groups.intendedFunctions.length || groups.sectors.length;

    const addActivity = () => setActivities(prev => [...prev, {
        tempId: nextTempId(), itemId: undefined, hours: emptyHours(),
        duration: "", reason: "", priority: "", description: "", address: {...emptyAddress(), city: entityCity},
    }]);

    const removeActivity = (tempId) => setActivities(prev => prev.filter(activity => activity.tempId !== tempId));

    const changeActivity = (tempId, patch) =>
        setActivities(prev => prev.map(activity => activity.tempId === tempId ? {...activity, ...patch} : activity));

    const editingHours = activities.find(activity => activity.tempId === hoursTempId)?.hours ?? emptyHours();
    const editingAddress = activities.find(activity => activity.tempId === addressTempId)?.address ?? emptyAddress();

    const validate = () => {
        if (!state.name) return "O campo \"Nome\" é obrigatório.";
        if (state.inactive && !state.inactiveObservation) return "O campo \"Motivo da inativação\" é obrigatório.";
        for (const activity of activities) {
            if (!activity.reason) return "Informe a natureza de todas as atividades.";
            if (!hasAddress(activity.address)) return "Informe ao menos uma opção de endereço em todas as atividades.";
        }
        return "";
    };

    const persist = ({addAfter = false, copy = false} = {}) => {
        const message = validate();
        if (message) {
            projectUtils.swalErrorMessage("", message, "Ok");
            return;
        }

        const model = buildScheduleModel({
            state: {...state, id}, groups, activities, attaches, attachesDescription, addNew: addAfter, addNewHome: false,
        });

        const formData = new FormData();
        formData.append("myModel", JSON.stringify(model));
        attaches.filter(file => file.new).forEach(file => formData.append("attachFiles", file));

        setIsSaving(true);
        const response = id ? ScheduleGarrisonService.update(id, formData) : ScheduleGarrisonService.save(formData);
        response
            .then((data) => {
                projectUtils.success(true);

                if (copy) {
                    navigate(`${API_DESPACHO_WEB}/schedule-garrison/form?cp=${data.id}`, false);
                    return;
                }
                if (addAfter) {
                    navigate(API_DESPACHO_WEB + "/schedule-garrison/form", false);
                    return;
                }
                if (id) {
                    navigate(API_DESPACHO_WEB + "/schedule-garrison", false);
                    return;
                }
                navigate(`${API_DESPACHO_WEB}/schedule-garrison/form/${data.id}`, false);
            })
            .catch((error) => {
                console.error(error);
                const errMessage = error.response?.data?.errorMessages ?? "Não foi possível salvar.";
                projectUtils.swalErrorMessage("", errMessage, "Ok");
            })
            .finally(() => setIsSaving(false));
    };

    const destroy = () => {
        projectUtils.swalMessage("warning", "Excluir", `Excluir a atividade programada "${state.name}"?`, "Sim", true, "Não",
            ({isConfirmed}) => {
                if (!isConfirmed) return;
                ScheduleGarrisonService.destroy(id)
                    .then(() => {
                        projectUtils.success(true, "Excluído com sucesso");
                        navigate(API_DESPACHO_WEB + "/schedule-garrison", false);
                    })
                    .catch((error) => {
                        console.error(error);
                        const errMessage = error.response?.data?.errorMessages ?? "Não foi possível excluir.";
                        projectUtils.swalErrorMessage("", errMessage, "Ok");
                    });
            });
    };

    const newSchedule = () => navigate(API_DESPACHO_WEB + "/schedule-garrison/form", false);

    return (
        <Template isLoading={isLoading || isSaving}>
            <Template.Header>
                <Template.Title browserTitle={'Atividade programada'}>Atividade programada {id ? <span className="text-[12px]">(ID: {id})</span> : ""}</Template.Title>
                {id &&
                    <Button target="primary-outline" onClick={newSchedule}><FaPlus/>Adicionar atividade programada</Button>
                }
            </Template.Header>
            <Template.Body className="flex flex-col space-y-2">
                <Card>
                    <Card.Header>
                        <Card.Title>Dados</Card.Title>
                    </Card.Header>
                    <Card.Body className="flex flex-col gap-4">
                        <SentryRow>
                            <InputForm
                                classNameWrapper="col-span-3"
                                inputClass={REQUIRED_BG}
                                labelClass={REQUIRED_LABEl_BG}
                                id="name"
                                label="Nome *"
                                value={state.name}
                                onChange={({target: {value}}) => setState(prev => ({...prev, name: value}))}
                            />

                            {id && data?.userCreated &&
                                <InputForm
                                    classNameWrapper="col-span-3"
                                    id="userCreated"
                                    label="Cadastro"
                                    value={data.userCreated + " em " + projectUtils.formatDate(data?.schedule?.systemCreation)}
                                    disabled
                                />
                            }

                            <div className="flex items-center col-span-6">
                                <div className="relative flex-1">
                                    <InputDatetime
                                        classNameWrapper="right-date-input"
                                        id="dateUsageMin"
                                        label="Período disponível"
                                        dateFormat="L"
                                        returnDateObj={false}
                                        selected={state.dateUsageMin}
                                        onChange={(value) => setState(prev => ({...prev, dateUsageMin: value ?? ""}))}
                                    />
                                </div>
                                <div
                                    className="border border-[#E5E7EB] px-4 py-2 text-sm leading-none">
                                    à
                                </div>
                                <div className="relative flex-1">
                                    <InputDatetime
                                        classNameWrapper="left-date-input"
                                        id="dateUsageMax"
                                        dateFormat="L"
                                        returnDateObj={false}
                                        selected={state.dateUsageMax}
                                        onChange={(value) => setState(prev => ({...prev, dateUsageMax: value ?? ""}))}
                                    />
                                </div>
                            </div>
                        </SentryRow>

                        <InputTextarea
                            id="description"
                            label="Descrição"
                            textAreaClass="h-25!"
                            value={state.description}
                            onChange={({target: {value}}) => setState(prev => ({...prev, description: value}))}
                        />

                        <InputCheckbox
                            id="inactive"
                            label="Inativar"
                            checked={state.inactive}
                            onChange={({target}) => setState(prev => ({...prev, inactive: target.checked}))}
                        />

                        {state.inactive &&
                            <InputTextarea
                                id="inactiveObservation"
                                label="Motivo da inativação *"
                                textAreaClass="h-25!"
                                value={state.inactiveObservation}
                                onChange={({target: {value}}) => setState(prev => ({...prev, inactiveObservation: value}))}
                            />
                        }
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Header>
                        <Card.Title>Restringir para as guarnições</Card.Title>
                    </Card.Header>
                    <Card.Body className="flex flex-col gap-4">
                        <SentryRow>
                            <SelectComponent
                                classNameWrapper="col-span-6"
                                label="Dias da semana"
                                isMulti
                                options={WEEKDAYS}
                                value={WEEKDAYS.filter(option => groups.weekdays.includes(option.value))}
                                onChange={(selected) => onGroupChange("weekdays", selected)}
                            />

                            <SelectComponent
                                classNameWrapper="col-span-6"
                                label="Equipe"
                                isMulti
                                options={teamOptions}
                                value={teamOptions.filter(option => groups.teams.includes(option.value))}
                                onChange={(selected) => onGroupChange("teams", selected)}
                            />

                            <SelectComponent
                                classNameWrapper="col-span-6"
                                label="Função destinada"
                                isMulti
                                options={intendedFunctionOptions}
                                value={intendedFunctionOptions.filter(option => groups.intendedFunctions.includes(option.value))}
                                onChange={(selected) => onGroupChange("intendedFunctions", selected)}
                            />

                            <SelectComponent
                                classNameWrapper="col-span-6"
                                label="Setor"
                                isMulti
                                options={sectorOptions}
                                value={sectorOptions.filter(option => groups.sectors.includes(option.value))}
                                onChange={(selected) => onGroupChange("sectors", selected)}
                            />
                        </SentryRow>
                        <InputCheckbox
                            id="auto-apply"
                            label="Atribuir automaticamente às guarnições (respeitando as restrições)"
                            disabled={!groupHasSelection}
                            checked={state.inactive ? false : groups.autoApply}
                            onChange={({target}) => setGroups(prev => ({...prev, autoApply: target.checked}))}
                        />
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Header className={"flex gap-2 items-center"}>
                        <Card.Title>Atividades</Card.Title>

                        <Button className={"h-7.5! w-7.5 p-0!"} onClick={addActivity}>
                            <FaPlus/>
                        </Button>
                    </Card.Header>
                    <Card.Body>
                        <ScheduleGarrisonActivities
                            activities={activities}
                            reasonOptions={reasonOptions}
                            priorityOptions={priorityOptions}
                            onRemove={removeActivity}
                            onChange={changeActivity}
                            onEditHours={setHoursTempId}
                            onEditAddress={setAddressTempId}
                            REQUIRED_BG={REQUIRED_BG}
                        />
                    </Card.Body>
                </Card>

                <Dropzone
                    title="Anexos"
                    attaches={attaches}
                    setAttaches={setAttaches}
                    attachesDescription={attachesDescription}
                    setAttachesDescription={setAttachesDescription}
                />

                <Card>
                    <Card.Body className="flex gap-2 justify-end">
                        {id && <Button target="danger" onClick={destroy} actionFeedbackType='delete'>Excluir</Button>}
                        {id && <Button target="primary-outline" onClick={() => persist({copy: true})} actionFeedbackType='save'>Salvar e copiar</Button>}
                        <Button target="primary-outline" onClick={() => persist({addAfter: true})} actionFeedbackType='save'>Salvar e adicionar novo</Button>
                        <Button onClick={() => persist()} actionFeedbackType='save'>Salvar</Button>
                    </Card.Body>
                </Card>
            </Template.Body>

            <ScheduleGarrisonHoursModal
                open={hoursTempId !== null}
                value={editingHours}
                setOpen={(value) => { if (!value) setHoursTempId(null); }}
                onSave={(hours) => changeActivity(hoursTempId, {hours})}
            />

            <ScheduleGarrisonAddressModal
                open={addressTempId !== null}
                value={editingAddress}
                entityCity={entityCity}
                setOpen={(value) => { if (!value) setAddressTempId(null); }}
                onSave={(address) => changeActivity(addressTempId, {address})}
            />
        </Template>
    );
};

export default ScheduleGarrisonForm;